import type { DocumentNode, OperationVariables, TypedDocumentNode } from "@apollo/client";
import { useLazyQuery } from "@apollo/client/react";

import { AdditionalHookOptions } from "./types";
import shouldPollHelper from "./shouldPoll";
import usePollingInterval from "./usePollingInterval";
import usePollingManagement from "./usePollingManagement";

const usePolledLazyQuery = <
  TData = unknown,
  TVariables extends OperationVariables = OperationVariables,
>(
  query: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: Omit<useLazyQuery.Options<NoInfer<TData>, NoInfer<TVariables>>, "returnPartialData"> &
    AdditionalHookOptions<TData>
): useLazyQuery.ResultTuple<TData, TVariables, "empty" | "complete" | "streaming"> => {
  const {
    pollInterval: overridePollInterval,
    pollingGroup,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    shouldPoll, // not needed in useLazyQuery
    ...restOptions
  } = options;
  // See the same-shaped cast + comment in ./index.ts for why this is needed. usePolledLazyQuery
  // never opts into returnPartialData (omitted from the options type above), so the result's
  // dataState is never "partial" - cast to narrow accordingly.
  const queryResult = useLazyQuery(
    query,
    restOptions as useLazyQuery.Options<TData, TVariables>
  ) as useLazyQuery.ResultTuple<TData, TVariables, "empty" | "complete" | "streaming">;

  const pollInterval = usePollingInterval(query, pollingGroup, overridePollInterval);

  const [execute, result] = queryResult;

  /* Disable polling management until the lazy query is executed.
   * Calling refetch/startPolling/stopPolling on an unexecuted lazy query
   * triggers Apollo to fire a request with empty variables.
   */
  const isPollingManagementDisabled = !result.called;

  usePollingManagement(
    pollInterval,
    result.startPolling,
    result.stopPolling,
    shouldPollHelper(options, result),
    result.refetch,
    isPollingManagementDisabled
  );

  // v4 throws if a consumer calls the raw `refetch` before the lazy query has ever executed
  // (e.g. a child component invoking a callback prop before this hook's own mount effect has
  // run - effects fire child-before-parent, so this is a real, not just theoretical, race).
  // There's nothing yet to *re*-fetch in that case, so execute the query for the first time
  // with the same variables instead - that's what the caller actually wants.
  const refetch: typeof result.refetch = (variables) =>
    result.called
      ? result.refetch(variables)
      : (execute({ variables } as useLazyQuery.ExecOptions<TVariables>) as unknown as ReturnType<
          typeof result.refetch
        >);

  return [execute, { ...result, refetch }] as useLazyQuery.ResultTuple<
    TData,
    TVariables,
    "empty" | "complete" | "streaming"
  >;
};

export default usePolledLazyQuery;
